import { groupBy, isEmpty } from "lodash/fp";

import useMetricsQuery from "@app/analytics/hooks/metrics/useMetricsQuery";
import * as Filters from "@app/analytics/meetops/filters";
import { MeetOpsQuery } from "@app/analytics/meetops/types";
import { EnterpriseWorkspaceTeam } from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import {
  ParticipationApiResponse,
  ParticipationTableData,
  TeamParticipationApiResponse,
} from "./types";
import { useWorkspaceOverview } from "../WorkspaceOverviewProvider";

const getBaseQuery = ({
  dateRanges,
  additionalDimensions = [],
}: {
  dateRanges: MeetOpsQuery["dateRanges"];
  additionalDimensions?: MeetOpsQuery["dimensions"];
}): MeetOpsQuery => ({
  dateRanges,
  dimensions: ["meetingId", ...additionalDimensions],
  metrics: ["meetingTalkTimeEntropy"],
  filters: [Filters.multiParticipantMeeting(), Filters.notNullTeams()],
  people: "everyone",
});

const LOW_PARTICIPATION_THRESHOLD = 70;

const getLowParticipationPercent = (meetings: ParticipationApiResponse[]) => {
  if (isEmpty(meetings)) return 0;

  const meetingsWithLowParticipation = meetings.filter(
    (meeting) =>
      (meeting.meetingTalkTimeEntropy.rawValue ?? 0) * 100 <
      LOW_PARTICIPATION_THRESHOLD
  );

  return Math.round(
    (meetingsWithLowParticipation.length / meetings.length) * 100
  );
};

const getTableData = (
  apiResults: TeamParticipationApiResponse[],
  teams: EnterpriseWorkspaceTeam[]
): ParticipationTableData[] => {
  if (isEmpty(apiResults)) return [];

  const meetingsGroupedByTeam = groupBy("teamId", apiResults);

  return teams.map((team) => {
    const meetings = meetingsGroupedByTeam[team.id] ?? [];

    return {
      ...team,
      numMeetings: meetings.length,
      percentMeetingsWithLowParticipation: getLowParticipationPercent(meetings),
    };
  });
};

const getOverallData = (
  apiResults: ParticipationApiResponse[]
): ParticipationTableData => ({
  id: "ALL_TEAMS",
  name: "Overall",
  numMeetings: apiResults.length,
  percentMeetingsWithLowParticipation: getLowParticipationPercent(apiResults),
});

const useParticipation = () => {
  const { queryDateRanges } = useWorkspaceOverview();
  const { workspace } = useWorkspace();

  const breakoutByTeamsQuery = useMetricsQuery(
    getBaseQuery({
      dateRanges: queryDateRanges,
      additionalDimensions: ["teamId"],
    }),
    {
      queryKey: ["participationByTeam", workspace.id],
      staleTime: 30 * 60 * 1000,
    }
  );

  const workspaceAverageQuery = useMetricsQuery(
    getBaseQuery({ dateRanges: queryDateRanges }),
    {
      queryKey: ["overallParticipation", workspace.id],
      staleTime: 30 * 60 * 1000,
    }
  );

  const refetchAll = () => {
    if (breakoutByTeamsQuery.isError) breakoutByTeamsQuery.refetch();
    if (workspaceAverageQuery.isError) workspaceAverageQuery.refetch();
  };

  const teamBreakoutResults = breakoutByTeamsQuery.data?.results[0].data ?? [];
  const workspaceResults = workspaceAverageQuery.data?.results[0].data ?? [];

  return {
    isInitialLoading:
      breakoutByTeamsQuery.isInitialLoading ||
      workspaceAverageQuery.isInitialLoading,
    isError: breakoutByTeamsQuery.isError || workspaceAverageQuery.isError,
    refetch: refetchAll,
    tableData: getTableData(teamBreakoutResults, workspace.teams),
    overallData: getOverallData(workspaceResults),
  };
};

export default useParticipation;
